import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import apiService from '../../services/ApiService';
import VehicleCard from '../VehicleCard';
import { vehicleProfilePath } from '../../utils/vehicleUrls';

type OwnerOtherBuildsProps = {
  ownerUsername: string;
  currentVehicleId: number;
  ownerDisplayName?: string;
};

/**
 * Other public builds from the same garage — replaces generic related-builds on the vehicle page.
 */
const OwnerOtherBuilds: React.FC<OwnerOtherBuildsProps> = ({
  ownerUsername,
  currentVehicleId,
  ownerDisplayName,
}) => {
  const [rows, setRows] = useState<any[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await apiService.getPublicUserProfile(ownerUsername);
        const vehicles = (data?.vehicles || []).filter(
          (v: { id: number; is_public?: boolean }) =>
            Number(v.id) !== Number(currentVehicleId) && v.is_public !== false
        );
        if (!cancelled) setRows(vehicles.slice(0, 6));
      } catch {
        if (!cancelled) setRows([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ownerUsername, currentVehicleId]);

  if (!rows.length) return null;

  const label = ownerDisplayName || `@${ownerUsername}`;

  return (
    <section className="mt-10" aria-labelledby="owner-other-builds-heading">
      <h2 id="owner-other-builds-heading" className="text-xl text-white mb-2">
        More from {label}
      </h2>
      <p className="text-sm text-gray-500 mb-4">Other public builds in this garage.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {rows.map((v) => (
          <VehicleCard
            key={v.id}
            collectionMode
            vehicle={{
              ...v,
              trim: v.trim || '',
              modification_count: v.modification_count || 0,
              display_name: v.display_name || v.nickname || `${v.year} ${v.make} ${v.model}`,
              created_at: v.created_at || '',
              owner_username: ownerUsername,
            }}
            discoveryBadges={v.badges}
            discoveryBadgeOverflow={v.badge_overflow || 0}
            limitedDesignationPrimary={(v as { limited_designation_primary?: any }).limited_designation_primary || null}
            onVehicleClick={(vehicle) =>
              navigate(
                vehicleProfilePath(
                  ownerUsername,
                  vehicle.public_slug || vehicle.id
                )
              )
            }
          />
        ))}
      </div>
    </section>
  );
};

export default OwnerOtherBuilds;
